import { Box } from "@mui/material";
import { useMemo } from "react";
import { Job } from "../interfaces/job.interfaces";
import { JobDetailHeader } from "./components/JobDetailsHeader";
import { JobsEvents } from "./events/JobsEvents";
import { JobsDetailsInfo } from "./components/JobsDetailsInfo";
import { JobContextRow } from "./components/JobContextRow";
import { ScopeOfWork } from "../../scope-of-work/types/scope-of-work.interface";
import { useModals } from "src/context/ModalContext";
import { useJobsByIdQuery } from "../jobs-list/jobs-layout/hooks/useJobByIdQuery";
import { JobServiceCostCard } from "./components/JobServiceCostCard";
import { resolveJobServiceCost } from "./utils/resolveJobServiceCost";
import { JobDefaultWorkforceSection } from "../jobs-list/jobs-layout/components/JobDefaultWorkforceSection";
import { JobDescriptionCard } from "./components/JobDescriptionCard";
import { JobNotesCard } from "./components/JobNotesCard";

interface Props {
  job: Job;
  scopeOfWork?: ScopeOfWork | null;
  backModalKey?: string;
  onParentJobUpdated?: () => void;
}

export function JobDetails({ job: initialJob, scopeOfWork: scopeFromHook, backModalKey, onParentJobUpdated }: Props) {
  const { modals, closeModal } = useModals();

  const { data: fetchedJob, refetch: refetchJob } = useJobsByIdQuery({ idStr: initialJob?.id });

  const job = useMemo(() => {
    const fetched = fetchedJob as unknown as Job | undefined;
    if (!fetched) return initialJob;

    const merged = { ...initialJob, ...fetched };

    const resolvedClient =
      fetched.client ??
      initialJob?.client ??
      (fetched as Job & { quote?: { client?: Job["client"] } }).quote?.client ??
      (initialJob as Job & { quote?: { client?: Job["client"] } }).quote?.client;

    if (resolvedClient) {
      merged.client = resolvedClient;
    }

    return merged;
  }, [fetchedJob, initialJob]);

  const effectiveScopeOfWork = useMemo(() => {
    if (!job) return undefined;

    const nested = (job as Job & { scopeOfWork?: ScopeOfWork | null }).scopeOfWork;

    return scopeFromHook ?? nested ?? undefined;
  }, [job, scopeFromHook]);

  const serviceCost = useMemo(() => {
    if (!job) return null;

    return resolveJobServiceCost(job, effectiveScopeOfWork);
  }, [job, effectiveScopeOfWork]);

  const clientUid = useMemo(() => {
    const client = job?.client ?? (job as Job & { quote?: { client?: Job["client"] } })?.quote?.client ?? job?.events?.[0]?.client ?? null;

    return job?.clientUid ?? client?.uid ?? null;
  }, [job]);

  const handleBack = () => {
    if (!backModalKey) return;

    const modalToClose = [...modals].reverse().find((modal) => modal.key === backModalKey && !modal.minimized);

    if (modalToClose) {
      closeModal(modalToClose.id);
    }
  };

  if (!job) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
      }}
    >
      <JobDetailHeader
        job={job}
        scopeOfWork={effectiveScopeOfWork}
        onJobUpdated={async () => {
          await refetchJob();
          await onParentJobUpdated?.();
        }}
      />

      <JobDescriptionCard
        jobId={job.id}
        description={job.description}
        onUpdated={() => {
          refetchJob();
        }}
      />

      <JobNotesCard jobId={job.id} clientUid={clientUid} />

      <JobServiceCostCard job={job} scopeOfWork={effectiveScopeOfWork} serviceCost={serviceCost} />

      <JobsDetailsInfo />

      <JobContextRow jobId={job.id} coverageArea={job?.coverageArea} clientUid={clientUid} />

      <JobDefaultWorkforceSection job={job} />

      <Box sx={{ mt: "auto" }}>
        <JobsEvents job={job} />
      </Box>
    </Box>
  );
}
